<script>
  import Panel from './Panel.svelte';
  import Alert from './Alert.svelte';
  import Button from './Button.svelte';
  import Code from './Code.svelte';
  import DataTable from './DataTable.svelte';
  import DistributionTable from './DistributionTable.svelte';
  import PeriodChips from './PeriodChips.svelte';
  import SegmentedControl from './SegmentedControl.svelte';
  import { allowedViews, VIEWS } from '../../../lib/value_schemes.js';

  // One stored query → one panel. The renderer keys on which result-shape
  // convention variables are present (series / publisher / period / value /
  // count); a result outside the convention falls back to a plain table, never
  // an error.
  //
  // Blocks are keyed on ?series when the query selects one, else on ?publisher.
  // A compiled comparison can put two cubes from the SAME institution side by
  // side, which is exactly what publisher-keyed blocks cannot express — they
  // would draw one heading over both cubes' numbers. Every hand-written query
  // predates ?series and is unaffected.
  //
  // The known block set is who could have answered, supplied rather than derived
  // from the rows: `query.series` for a comparison (the labels its author gave
  // the sides), `publishers` for a publisher-keyed panel (the own institution
  // plus every readable partner, from the analysis job). A side that answered
  // nothing has no row to derive a heading from, and an omitted block reads as
  // "not part of this comparison" when it means "answers nothing here".
  //
  // The panel draws in one of the views (lib/value_schemes.js). Separate:
  // blocks sit side by side, and each keeps its own vocabulary, its own
  // denominator and its own percentages: nothing is pivoted onto a shared row
  // axis, so nothing there depends on aligning "Female" with "Femenino".
  // Aligned: one row per answer of the term's value scheme, each side's share
  // on it, so it needs that scheme. The panel opens in the view its author
  // saved (`query.view`); the switch changes it until the next run, no longer.
  // Scale: one bar per side along a scheme that declares two opposite ends,
  // the low end left of the middle, the high end right of it, with the mean.
  //
  // `periodsAddUp` false (survey rounds) drops "All periods": the panel opens
  // on the latest period value and shows one at a time.
  // `onedit` (query) → void: the author's way back to the query, null where
  // there is none (a preview, or a login that may not save queries).
  let { query, result, publishers = [], periodsAddUp = true, onedit = null } = $props();

  let chosenPeriod = $state(null);
  let chosenView = $state(null);

  const num = (v) => Number(v) || 0;
  const cols = $derived(result.columns ?? []);
  const hasSeries = $derived(cols.includes('series'));
  const hasPublisher = $derived(cols.includes('publisher'));
  const keyVar = $derived(hasSeries ? 'series' : hasPublisher ? 'publisher' : null);
  const hasPeriod = $derived(cols.includes('period'));
  const convention = $derived(cols.includes('value') && cols.includes('count'));

  const views = $derived(convention && keyVar ? allowedViews(result.scheme) : ['separate']);
  const view = $derived(chosenView ?? (views.includes(query.view) ? query.view : 'separate'));

  // A period a side withholds whole still is a period of the comparison.
  const periods = $derived(hasPeriod
    ? [...new Set([...result.rows.map((r) => r.period), ...(result.withheld ?? []).flatMap((w) => w.periods)])].sort()
    : []);
  const selectedPeriod = $derived(chosenPeriod ?? (periodsAddUp ? 'all' : periods.at(-1) ?? 'all'));

  // What a side's manifest says it withholds for this question, by publisher;
  // in a panel of several questions, by question and publisher.
  const withheldBy = $derived(new Map((result.withheld ?? []).map((w) => [`${w.group ?? ''}|${w.publisher}`, w])));
  const unit = $derived(query.unit ?? 'records');
  // Why a block has no rows, or which periods it leaves out under "All periods".
  // `question` is the group a note is for, in a panel of several questions.
  function withheldNote(group, question = null) {
    // A builder comparison names its blocks; its sides say whose they are.
    const publisher = hasSeries ? query.sides?.find((s) => s.label === group.label)?.publisher : group.label;
    const w = publisher ? withheldBy.get(`${question ?? ''}|${publisher}`) : null;
    if (!w?.periods.length) return null;
    const fewer = `fewer than ${w.minPerColumn} ${unit}`;
    if (selectedPeriod !== 'all') return w.periods.includes(selectedPeriod) ? `${group.label} shares no figures for ${selectedPeriod}: ${fewer} there.` : null;
    return group.total ? `Without ${w.periods.join(', ')}: ${fewer} there.` : `${group.label} shares no figures: ${fewer} in ${w.periods.join(', ')}.`;
  }

  // The rows of the selected period (or all of them), the ones every view
  // reads. "All periods" sums a value across every quarter.
  const periodRows = $derived(!convention ? []
    : hasPeriod && selectedPeriod !== 'all' ? result.rows.filter((r) => r.period === selectedPeriod) : result.rows);

  // Aggregate rows to one block per side (publisher or series), value → count.
  function blocksOf(rows) {
    const byBlock = new Map();
    for (const r of rows) {
      const key = keyVar ? r[keyVar] : null;
      if (!byBlock.has(key)) byBlock.set(key, new Map());
      const m = byBlock.get(key);
      m.set(r.value, (m.get(r.value) ?? 0) + num(r.count));
    }
    // Known blocks first, in the order they were declared (the author's side
    // order for a comparison; own institution then partners otherwise); any key
    // in the rows that is not in that list still gets a block — dropping data to
    // keep a tidy column set would be the worse failure of the two.
    const known = hasSeries
      ? (query.series ?? [])
      : hasPublisher ? publishers.map((p) => p.label) : [null];
    const order = [...known, ...[...byBlock.keys()].filter((k) => !known.includes(k))];

    return order.map((label) => {
      const m = byBlock.get(label) ?? new Map();
      // `values` is a one-element array so the rows go straight into the shared
      // DistributionTable, which takes one cell per column and here has one.
      const items = result.scheme ? schemeItems(m) : [...m.entries()].map(([value, count]) => ({ values: [value], count }))
        .sort((a, b) => b.count - a.count);
      return { label, total: items.reduce((s, i) => s + i.count, 0), items, counts: m };
    });
  }

  // Every side over all its rows: what the notes about a whole side read.
  const groups = $derived(blocksOf(periodRows));

  // A query that also selects ?group (a cohort) is drawn as one comparison per
  // group, in the group's name order, each with its own n and shares per side:
  // a cohort's profile is read against the same cohort elsewhere, not against
  // the side's other cohorts. Without ?group, one section holds everything.
  // A panel of several questions (?group bound to catalog terms) has its
  // groups listed in the result, in questionnaire order with their text, and
  // each one's questionnaire section; a group then shows even in a period it
  // has no rows for, which is when its withheld note matters.
  const hasGroup = $derived(cols.includes('group'));
  const listed = $derived(result.groups ?? null);
  const sections = $derived.by(() => {
    const names = !hasGroup ? [null] : listed ? listed.map((g) => g.value)
      : [...new Set(periodRows.map((r) => r.group))].sort((a, b) => String(a).localeCompare(String(b)));
    return names.map((name, i) => {
      const info = listed?.[i] ?? null;
      const section = info?.section && info.section !== listed[i - 1]?.section
        ? (result.sections ?? []).find((s) => s.id === info.section) ?? null : null;
      const blocks = hasGroup ? blocksOf(periodRows.filter((r) => r.group === name)) : groups;
      const answering = blocks.filter((g) => g.total);
      const aligned = alignedOf(answering);
      return { key: name, label: info?.label ?? name, construct: info?.construct ?? null, heading: section,
        question: listed ? name : null, groups: blocks, answering, aligned, scaleRows: scaleOf(answering),
        // The longest bar is the largest share present, as in DistributionTable.
        maxShare: Math.max(0.01, ...aligned.flatMap((a) => a.shares.map((s) => s.share))) };
    });
  });

  // Aligned: the scheme's answers in order, then any answer it does not list
  // (every answer, for an open scheme such as country codes) largest first by
  // its average share across the sides, then the scheme's remainder (OTHER,
  // unknown). One row each, with a cell per side that has figures. A side
  // without any is left out of the table and says why underneath.
  function alignedOf(answering) {
    if (view !== 'aligned' || !result.scheme) return [];
    const { values, remainder } = result.scheme;
    const inScheme = new Set([...values, ...remainder].map((v) => v.value));
    const meanShare = (v) => answering.reduce((a, g) => a + (g.counts.get(v) ?? 0) / g.total, 0);
    const other = [...new Set(answering.flatMap((g) => [...g.counts.keys()]))].filter((v) => !inScheme.has(v))
      .sort((a, b) => meanShare(b) - meanShare(a));
    const answers = [...values, ...other.map((value) => ({ value, label: value, code: null })), ...remainder];
    return answers.map((a) => ({ ...a, shares: answering.map((g) => {
      const count = g.counts.get(a.value) ?? 0;
      return { count, share: count / g.total };
    }) }));
  }
  // Scale: per side, every answer of the scheme as one segment of a bar whose
  // middle (the scheme's middle answer, or the line between the ends) sits at
  // the centre, so the sides read against one axis. Half the track is 100%.
  // Answers the scheme does not know are counted apart and said so.
  const SIDE_COLOR = { low: 'var(--scale-low)', middle: 'var(--scale-middle)', high: 'var(--accent)' };
  function scaleOf(answering) {
    if (view !== 'scale' || !result.scheme?.scale) return [];
    const { low, middle = [], high } = result.scheme.scale;
    const sideOf = (code) => (low.includes(code) ? 'low' : high.includes(code) ? 'high' : 'middle');
    // Extreme answers in full colour, those nearer the middle lighter.
    const strength = (code) => {
      const run = sideOf(code) === 'low' ? [...low] : sideOf(code) === 'high' ? [...high].reverse() : middle;
      return run.length > 1 ? 100 - (45 * run.indexOf(code)) / (run.length - 1) : 100;
    };
    const numeric = result.scheme.values.every((v) => Number.isFinite(Number(v.code)));
    return answering.map((g) => {
      const counts = result.scheme.values.map((v) => g.counts.get(v.value) ?? 0);
      const n = counts.reduce((a, b) => a + b, 0);
      const share = (code) => (n ? (counts[result.scheme.values.findIndex((v) => v.code === code)] / n) : 0);
      const sum = (codes) => codes.reduce((a, c) => a + share(c), 0);
      let left = 50 - (sum(low) + sum(middle) / 2) * 50;
      const segments = result.scheme.values.map((v, i) => {
        const seg = { label: v.label, count: counts[i], share: n ? counts[i] / n : 0, left,
          color: `color-mix(in srgb, ${SIDE_COLOR[sideOf(v.code)]} ${strength(v.code)}%, white)` };
        left += seg.share * 50;
        return seg;
      });
      return { label: g.label, n, other: g.total - n, segments, low: sum(low), middle: sum(middle), high: sum(high),
        mean: numeric && n ? result.scheme.values.reduce((a, v, i) => a + Number(v.code) * counts[i], 0) / n : null };
    });
  }

  const pct = (share) => `${(share * 100).toFixed(1)}%`;

  // With the term's value scheme every block lists every value, in the
  // scheme's order and under its label, zeros included: row n is the same
  // answer in every block. A value the scheme does not list (any value, for an
  // open scheme) still shows, after the scheme's, largest first; the scheme's
  // remainder (OTHER, unknown) comes last.
  function schemeItems(counts) {
    const item = (v) => ({ values: [v.label], codes: [v.code], count: counts.get(v.value) ?? 0 });
    const { values, remainder } = result.scheme;
    const inScheme = new Set([...values, ...remainder].map((v) => v.value));
    const other = [...counts.entries()].filter(([value]) => !inScheme.has(value))
      .map(([value, count]) => ({ values: [value], count })).sort((a, b) => b.count - a.count);
    return [...values.map(item), ...other, ...remainder.map(item)];
  }

</script>

<Panel title={query.label}>
  {#if query.description}<p class="wf2-caption">{query.description}</p>{/if}
  {#if result.schemas?.length}<p class="wf2-caption">From {result.schemas.join('; ')}</p>{/if}

  {#if result.error}
    <Alert title="This query failed" variant="danger">
      <p class="mono">{result.error}</p>
    </Alert>
  {:else if !convention}
    <!-- Outside the ?publisher ?value ?period ?count convention: plain table. -->
    {#if result.rows.length}
      <DataTable columns={cols} rows={result.rows.map((r) => cols.map((c) => r[c]))} />
    {:else}
      <p class="muted">No rows.</p>
    {/if}
  {:else}
    <!-- What to show on the left (the period), how to show it on the right. -->
    <div class="toolbar">
      {#if hasPeriod}
        <PeriodChips {periods} selected={selectedPeriod} all={periodsAddUp} onselect={(p) => (chosenPeriod = p)} />
      {/if}
      {#if views.length > 1}
        <div class="view-as">
          <SegmentedControl label="View as" selected={view} onselect={(v) => (chosenView = v)}
            options={views.map((v) => ({ id: v, label: VIEWS[v] }))} />
        </div>
      {/if}
    </div>

    {#each sections as sec}
      {#if sec.heading}
        <h3 class="wf2-title wf2-title-level-3 part">{sec.heading.label}</h3>
        {#if sec.heading.note}<p class="wf2-caption">{sec.heading.note}</p>{/if}
      {/if}
      {#if sec.construct}<p class="construct wf2-caption">{sec.construct}</p>{/if}
      {#if sec.label != null}<h4 class="wf2-title wf2-title-level-4 section">{sec.label}</h4>{/if}
      {#if view === 'separate'}
        <div class="blocks" style="--cols:{Math.max(1, sec.groups.length)}">
          {#each sec.groups as group}
            <div class="block">
              {#if keyVar}
                <h4 class="wf2-title wf2-title-level-4">
                  {group.label}{group.total ? ` · ${group.total} ${unit}` : ''}
                </h4>
              {/if}
              {#if group.total}
                <DistributionTable columns={['Value']} rows={group.items} total={group.total} />
                {#if (!hasGroup || sec.question) && withheldNote(group, sec.question)}<p class="wf2-caption">{withheldNote(group, sec.question)}</p>{/if}
              {:else if (!hasGroup || sec.question) && withheldNote(group, sec.question)}
                <!-- Withheld, not missing: the side publishes this question, with
                     too few answers in this period to share them. -->
                <p class="empty muted">{withheldNote(group, sec.question)}</p>
              {:else}
                <!-- Present and labelled, not omitted. The block is the statement:
                     this side of the comparison answers nothing here. -->
                <p class="empty muted">
                  {sec.question ? `${group.label} has no answers to share here` : sec.label != null ? `${group.label} has nothing for ${sec.label}` : hasSeries
                    ? `${group.label} returns nothing`
                    : `${group.label} shares no counts for this comparison`}{#if hasPeriod && selectedPeriod !== 'all'} for {selectedPeriod}{/if}.
                </p>
              {/if}
            </div>
          {/each}
        </div>
      {:else if view === 'scale'}
        {#if sec.scaleRows.length}
          <div class="scale">
            {#each sec.scaleRows as r}
              <div class="scale-row">
                <div>{r.label} <span class="wf2-caption">· {r.n} {unit}</span></div>
                <span class="end low" title="{result.scheme.values[0].label} side">{pct(r.low)}</span>
                <div class="track" title="middle {pct(r.middle)}">
                  <div class="axis"></div>
                  {#each r.segments as s}
                    {#if s.share}<div class="segment" style="left:{s.left}%; width:{s.share * 50}%; background:{s.color}" title="{s.label}: {pct(s.share)} ({s.count} {unit})"></div>{/if}
                  {/each}
                </div>
                <span class="end">{pct(r.high)}</span>
                <span class="mean">{#if r.mean != null}mean {r.mean.toFixed(2)}{/if}</span>
              </div>
              {#if r.other}<p class="wf2-caption">{r.label}: {r.other} {unit} not on the scale are left out.</p>{/if}
            {/each}
          </div>
        {/if}
      {:else if view === 'aligned'}
        {#if sec.answering.length}
          <div class="wf2-tablewrap" style="overflow-x:auto">
            <table class="wf2-table aligned">
              <thead>
                <tr>
                  <th>Value</th>
                  {#each sec.answering as g, i}
                    <th><span class="swatch side-{Math.min(i, 1)}"></span>{g.label} · {g.total} {unit}</th>
                  {/each}
                </tr>
              </thead>
              <tbody>
                {#each sec.aligned as a}
                  <tr>
                    {#if a.code}<td title={a.code}>{a.label}</td>{:else}<td class="mono">{a.label}</td>{/if}
                    {#each a.shares as s, i}
                      <td title="{s.count} {unit}">
                        <div class="bar-track"><div class="bar side-{Math.min(i, 1)}" style="width:{(s.share / sec.maxShare) * 100}%"></div></div>
                        <span class="wf2-caption">{pct(s.share)}</span>
                      </td>
                    {/each}
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        {/if}
      {/if}
      {#if view !== 'separate' && sec.question}
        {#each sec.groups as group}
          {#if withheldNote(group, sec.question)}
            <p class="wf2-caption">{withheldNote(group, sec.question)}</p>
          {:else if !group.total && groups.find((all) => all.label === group.label)?.total}
            <p class="wf2-caption">{group.label} has no answers to share here.</p>
          {/if}
        {/each}
      {:else if view !== 'separate' && sec.label != null}
        {#each sec.groups.filter((g) => !g.total && groups.find((all) => all.label === g.label)?.total) as group}
          <p class="wf2-caption">{group.label} has nothing for {sec.label}.</p>
        {/each}
      {/if}
    {/each}
    <!-- The scale's answers, once for the whole panel. -->
    {#if view === 'scale' && sections.find((x) => x.scaleRows.length)}
      <div class="legend wf2-caption">
        {#each sections.find((x) => x.scaleRows.length).scaleRows[0].segments as s}
          <span><span class="swatch" style="background:{s.color}"></span>{s.label}</span>
        {/each}
      </div>
    {/if}
    {#if view !== 'separate' || hasGroup}
      {#each groups as group}
        {#if withheldNote(group)}
          <p class="wf2-caption">{withheldNote(group)}</p>
        {:else if !group.total && keyVar}
          <p class="wf2-caption">{group.label} {hasSeries ? 'returns nothing' : 'shares no counts for this comparison'}{#if hasPeriod && selectedPeriod !== 'all'} for {selectedPeriod}{/if}.</p>
        {/if}
      {/each}
    {/if}
    {#if !groups.some((g) => g.total)}
      <p class="muted">No publisher returned rows for this selection.</p>
    {/if}
  {/if}

  {#if onedit}
    <div class="wf2-row" style="gap:12px; margin-top: var(--space-2)">
      <Button variant="ghost" onclick={() => onedit(query)}>Edit</Button>
    </div>
  {/if}
</Panel>

<style>
  /* One column per publisher, side by side — the comparison the whole federated
     read exists to make. minmax(0,1fr) rather than 1fr so a wide value label
     scrolls inside its own table instead of pushing the grid past the page. */
  .blocks {
    display: grid;
    grid-template-columns: repeat(var(--cols), minmax(0, 1fr));
    gap: var(--space-4);
    margin-bottom: var(--space-3);
  }
  /* Two tables do not fit narrow; below the app's breakpoint they stack. */
  @media (max-width: 720px) {
    .blocks { grid-template-columns: 1fr; }
  }
  .toolbar { display: flex; flex-wrap: wrap; align-items: center; gap: var(--space-2) var(--space-4); }
  .view-as { margin-left: auto; margin-bottom: var(--space-3); }
  h4.section { margin: var(--space-3) 0 var(--space-2); }
  /* A questionnaire section over its questions, and what a question measures
     just above its text. */
  h3.part { margin: var(--space-4) 0 var(--space-1); }
  .construct { margin: var(--space-3) 0 0; text-transform: uppercase; letter-spacing: .04em; font-size: .75rem; }
  .construct + h4.section { margin-top: 2px; }
  /* Aligned: the first side (the own institution, or the author's first
     series) in the accent, every other side in the second accent. */
  .aligned td { min-width: 9rem; }
  .bar.side-1 { background: var(--accent-2); }
  .swatch { display: inline-block; width: 10px; height: 10px; border-radius: 999px;
    background: var(--accent); margin-right: 6px; vertical-align: baseline; }
  .swatch.side-1 { background: var(--accent-2); }
  /* Scale: the low end in a warm colour, the middle grey, the high end in the
     accent; extreme answers in full, the ones nearer the middle lighter. */
  .scale, .legend { --scale-low: #d9480f; --scale-middle: #9ca3af; }
  .scale { margin-bottom: var(--space-3); }
  .scale-row { display: grid; grid-template-columns: 16rem 3.5rem 1fr 3.5rem 6rem;
    align-items: center; gap: var(--space-2); padding: 6px 0; }
  .scale .end { font-size: .85rem; text-align: right; }
  .scale .end:not(.low) { text-align: left; }
  .scale .mean { font-size: .85rem; color: var(--muted); }
  .track { position: relative; height: 18px; background: var(--surface-2); border-radius: 4px; }
  .axis { position: absolute; left: 50%; top: -4px; bottom: -4px; border-left: 1px solid var(--text); opacity: .5; }
  .segment { position: absolute; top: 0; bottom: 0; }
  .legend { display: flex; flex-wrap: wrap; gap: 4px 14px; margin-top: var(--space-2); }
  .legend .swatch { border-radius: 2px; }
  @media (max-width: 720px) {
    .scale-row { grid-template-columns: 1fr 3rem 2fr 3rem; }
    .scale .mean { grid-column: 1 / -1; }
  }
  .empty {
    border: 1px dashed var(--border);
    border-radius: var(--radius);
    padding: var(--space-3);
    font-size: .9rem;
  }
</style>
